import type { Language, Locale } from '@/config/locales';

export interface QrSessionTranslation {
  noQrCode: string;
  pickupNotSupported: string;
  unknownQrType: string;
  unknownError: string;
  sessionStartFailed: string;
  gpsChecking?: string;
  gpsTooFar?: string;
  gpsUnavailable?: string;
  verifying: string;
  startingSession: string;
  retry: string;
  goHome: string;
  tableLabel: (display: string, capacity: number) => string;
  guestCount: string;
  startOrder: string;
  cancel: string;
  sessionCompletedTitle: string;
  sessionExpiredTitle: string;
  sessionInvalidTitle: string;
  sessionUnknownTitle: string;
  sessionExpiredInline: string;
  sessionExpiredDescription: string;
  sessionExpiredGoHome: string;
  rescanQr: string;
  sessionTerminalReferenceLabel: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수). 키 누락 시 컴파일 에러로 강제.
export type QrSessionTranslations =
  & Record<Language, QrSessionTranslation>
  & Record<Exclude<Locale, Language>, QrSessionTranslation>;

export const qrSessionTranslations: QrSessionTranslations = {
  ko: {
    noQrCode: 'QR 코드가 없습니다.',
    pickupNotSupported: '이 매장은 현재 픽업을 지원하지 않습니다.',
    unknownQrType: '지원하지 않는 QR 코드 유형입니다.',
    unknownError: '알 수 없는 오류가 발생했습니다.',
    sessionStartFailed: '세션을 시작할 수 없습니다.',
    gpsChecking: '매장과의 거리를 확인하는 중…',
    gpsTooFar: '매장에서 너무 멉니다. 가까이 이동한 뒤 다시 시도해 주세요.',
    gpsUnavailable: '위치를 확인할 수 없습니다. 직원에게 POS에서 세션을 열어 달라고 요청해 주세요.',
    verifying: 'QR 코드 확인 중...',
    startingSession: '세션 시작 중...',
    retry: '다시 시도',
    goHome: '홈으로 돌아가기',
    tableLabel: (display, capacity) => `테이블 ${display} (좌석 ${capacity}석)`,
    guestCount: '인원 수',
    startOrder: '주문 시작하기',
    cancel: '취소',
    sessionCompletedTitle: '테이블 이용이 종료되었습니다.',
    sessionExpiredTitle: '이용 시간이 만료되었습니다.',
    sessionInvalidTitle: '이 세션은 더 이상 유효하지 않습니다.',
    sessionUnknownTitle: '세션을 확인할 수 없습니다.',
    sessionExpiredInline: '세션이 만료되었습니다. 다시 QR을 스캔해 주세요.',
    sessionExpiredDescription: '테이블 이용 시간이 종료되었습니다. 계속 이용하시려면 QR 코드를 다시 스캔해주세요.',
    sessionExpiredGoHome: '홈으로 이동',
    rescanQr: '다시 스캔하기',
    sessionTerminalReferenceLabel: '참조 코드',
  },
  es: {
    noQrCode: 'No se proporcionó código QR.',
    pickupNotSupported: 'Esta tienda no admite pedidos para recoger actualmente.',
    unknownQrType: 'Tipo de código QR no compatible.',
    unknownError: 'Ocurrió un error desconocido.',
    sessionStartFailed: 'No se pudo iniciar la sesión.',
    gpsChecking: 'Comprobando tu ubicación…',
    gpsTooFar: 'Estás demasiado lejos. Acércate al establecimiento e inténtalo de nuevo.',
    gpsUnavailable: 'No pudimos verificar tu ubicación. Pide al personal que abra la sesión en el POS.',
    verifying: 'Verificando código QR...',
    startingSession: 'Iniciando sesión...',
    retry: 'Reintentar',
    goHome: 'Volver al inicio',
    tableLabel: (display, capacity) => `Mesa ${display} (${capacity} asientos)`,
    guestCount: 'Número de comensales',
    startOrder: 'Comenzar pedido',
    cancel: 'Cancelar',
    sessionCompletedTitle: 'La sesión de mesa ha finalizado.',
    sessionExpiredTitle: 'El tiempo de uso ha expirado.',
    sessionInvalidTitle: 'Esta sesión ya no es válida.',
    sessionUnknownTitle: 'No se pudo verificar la sesión.',
    sessionExpiredInline: 'La sesión ha expirado. Escanee el QR nuevamente.',
    sessionExpiredDescription: 'Su sesión de mesa ha finalizado. Escanee el código QR nuevamente para continuar.',
    sessionExpiredGoHome: 'Ir al inicio',
    rescanQr: 'Escanear de nuevo',
    sessionTerminalReferenceLabel: 'Código de referencia',
  },
  en: {
    noQrCode: 'No QR code provided.',
    pickupNotSupported: 'This store does not support pickup right now.',
    unknownQrType: 'Unsupported QR code type.',
    unknownError: 'An unknown error occurred.',
    sessionStartFailed: 'Could not start session.',
    gpsChecking: 'Checking your location…',
    gpsTooFar: 'You are too far from the restaurant. Move closer and try again.',
    gpsUnavailable: 'We could not verify your location. Ask staff to open the session at the POS.',
    verifying: 'Verifying QR code...',
    startingSession: 'Starting session...',
    retry: 'Retry',
    goHome: 'Back to home',
    tableLabel: (display, capacity) => `Table ${display} (${capacity} seats)`,
    guestCount: 'Number of guests',
    startOrder: 'Start order',
    cancel: 'Cancel',
    sessionCompletedTitle: 'Your table session has ended.',
    sessionExpiredTitle: 'Your session time has expired.',
    sessionInvalidTitle: 'This session is no longer valid.',
    sessionUnknownTitle: "We couldn't verify this session.",
    sessionExpiredInline: 'Session expired. Please scan the QR again.',
    sessionExpiredDescription: 'Your table session has ended. Please scan the QR code again to continue.',
    sessionExpiredGoHome: 'Go to Home',
    rescanQr: 'Scan again',
    sessionTerminalReferenceLabel: 'Reference code',
  },
  pt: {
    noQrCode: 'Nenhum código QR fornecido.',
    pickupNotSupported: 'Esta loja não aceita pedidos para retirada no momento.',
    unknownQrType: 'Tipo de código QR não suportado.',
    unknownError: 'Ocorreu um erro desconhecido.',
    sessionStartFailed: 'Não foi possível iniciar a sessão.',
    verifying: 'Verificando código QR...',
    startingSession: 'Iniciando sessão...',
    retry: 'Tentar novamente',
    goHome: 'Voltar ao início',
    tableLabel: (display, capacity) => `Mesa ${display} (${capacity} lugares)`,
    guestCount: 'Número de pessoas',
    startOrder: 'Iniciar pedido',
    cancel: 'Cancelar',
    sessionCompletedTitle: 'A sessão da mesa foi encerrada.',
    sessionExpiredTitle: 'O tempo de uso expirou.',
    sessionInvalidTitle: 'Esta sessão não é mais válida.',
    sessionUnknownTitle: 'Não foi possível verificar a sessão.',
    sessionExpiredInline: 'A sessão expirou. Escaneie o QR novamente.',
    sessionExpiredDescription: 'Sua sessão de mesa terminou. Escaneie o código QR novamente para continuar.',
    sessionExpiredGoHome: 'Ir para o início',
    rescanQr: 'Escanear novamente',
    sessionTerminalReferenceLabel: 'Código de referência',
  },
  fr: {
    noQrCode: 'Aucun code QR fourni.',
    pickupNotSupported: 'Ce magasin ne prend pas en charge la commande à emporter pour le moment.',
    unknownQrType: 'Type de code QR non pris en charge.',
    unknownError: 'Une erreur inconnue est survenue.',
    sessionStartFailed: 'Impossible de démarrer la session.',
    verifying: 'Vérification du code QR...',
    startingSession: 'Démarrage de la session...',
    retry: 'Réessayer',
    goHome: "Retour à l'accueil",
    tableLabel: (display, capacity) => `Table ${display} (${capacity} places)`,
    guestCount: 'Nombre de convives',
    startOrder: 'Commencer la commande',
    cancel: 'Annuler',
    sessionCompletedTitle: 'La session de table est terminée.',
    sessionExpiredTitle: "Le temps d'utilisation a expiré.",
    sessionInvalidTitle: "Cette session n'est plus valide.",
    sessionUnknownTitle: 'Impossible de vérifier la session.',
    sessionExpiredInline: 'La session a expiré. Veuillez scanner à nouveau le QR.',
    sessionExpiredDescription: 'Votre session de table est terminée. Veuillez scanner à nouveau le code QR pour continuer.',
    sessionExpiredGoHome: "Aller à l'accueil",
    rescanQr: 'Scanner à nouveau',
    sessionTerminalReferenceLabel: 'Code de référence',
  },
  zh: {
    noQrCode: '未提供二维码。',
    pickupNotSupported: '该门店暂不支持自取订单。',
    unknownQrType: '不支持的二维码类型。',
    unknownError: '发生未知错误。',
    sessionStartFailed: '无法开始会话。',
    verifying: '正在验证二维码...',
    startingSession: '正在开始会话...',
    retry: '重试',
    goHome: '返回首页',
    tableLabel: (display, capacity) => `餐桌 ${display}（${capacity} 座）`,
    guestCount: '用餐人数',
    startOrder: '开始点餐',
    cancel: '取消',
    sessionCompletedTitle: '餐桌使用已结束。',
    sessionExpiredTitle: '用餐时间已到。',
    sessionInvalidTitle: '此会话已失效。',
    sessionUnknownTitle: '无法确认会话。',
    sessionExpiredInline: '会话已过期，请重新扫描二维码。',
    sessionExpiredDescription: '您的餐桌会话已结束。请重新扫描二维码以继续。',
    sessionExpiredGoHome: '前往首页',
    rescanQr: '重新扫描',
    sessionTerminalReferenceLabel: '参考代码',
  },
  ja: {
    noQrCode: 'QRコードがありません。',
    pickupNotSupported: 'この店舗は現在、持ち帰り注文に対応していません。',
    unknownQrType: '対応していないQRコードの種類です。',
    unknownError: '不明なエラーが発生しました。',
    sessionStartFailed: 'セッションを開始できませんでした。',
    verifying: 'QRコードを確認中...',
    startingSession: 'セッションを開始中...',
    retry: '再試行',
    goHome: 'ホームに戻る',
    tableLabel: (display, capacity) => `テーブル ${display}（${capacity}席）`,
    guestCount: '人数',
    startOrder: '注文を開始',
    cancel: 'キャンセル',
    sessionCompletedTitle: 'テーブルのご利用が終了しました。',
    sessionExpiredTitle: 'ご利用時間が終了しました。',
    sessionInvalidTitle: 'このセッションは無効です。',
    sessionUnknownTitle: 'セッションを確認できません。',
    sessionExpiredInline: 'セッションの有効期限が切れました。QRを再度スキャンしてください。',
    sessionExpiredDescription: 'テーブルのご利用時間が終了しました。続けるにはQRコードを再度スキャンしてください。',
    sessionExpiredGoHome: 'ホームへ移動',
    rescanQr: '再スキャン',
    sessionTerminalReferenceLabel: '参照コード',
  },
  hi: {
    noQrCode: 'कोई QR कोड प्रदान नहीं किया गया।',
    pickupNotSupported: 'यह स्टोर अभी पिकअप ऑर्डर का समर्थन नहीं करता है।',
    unknownQrType: 'असमर्थित QR कोड प्रकार।',
    unknownError: 'एक अज्ञात त्रुटि हुई।',
    sessionStartFailed: 'सत्र शुरू नहीं हो सका।',
    verifying: 'QR कोड सत्यापित किया जा रहा है...',
    startingSession: 'सत्र शुरू हो रहा है...',
    retry: 'पुनः प्रयास करें',
    goHome: 'होम पर वापस जाएं',
    tableLabel: (display, capacity) => `टेबल ${display} (${capacity} सीटें)`,
    guestCount: 'मेहमानों की संख्या',
    startOrder: 'ऑर्डर शुरू करें',
    cancel: 'रद्द करें',
    sessionCompletedTitle: 'टेबल का उपयोग समाप्त हो गया है।',
    sessionExpiredTitle: 'उपयोग का समय समाप्त हो गया है।',
    sessionInvalidTitle: 'यह सत्र अब मान्य नहीं है।',
    sessionUnknownTitle: 'सत्र सत्यापित नहीं किया जा सका।',
    sessionExpiredInline: 'सत्र समाप्त हो गया। कृपया QR को फिर से स्कैन करें।',
    sessionExpiredDescription: 'आपका टेबल सत्र समाप्त हो गया है। जारी रखने के लिए कृपया QR कोड को फिर से स्कैन करें।',
    sessionExpiredGoHome: 'होम पर जाएं',
    rescanQr: 'फिर से स्कैन करें',
    sessionTerminalReferenceLabel: 'संदर्भ कोड',
  },
  de: {
    noQrCode: 'Kein QR-Code angegeben.',
    pickupNotSupported: 'Dieses Geschäft unterstützt derzeit keine Abholbestellungen.',
    unknownQrType: 'Nicht unterstützter QR-Code-Typ.',
    unknownError: 'Ein unbekannter Fehler ist aufgetreten.',
    sessionStartFailed: 'Sitzung konnte nicht gestartet werden.',
    verifying: 'QR-Code wird überprüft...',
    startingSession: 'Sitzung wird gestartet...',
    retry: 'Erneut versuchen',
    goHome: 'Zurück zur Startseite',
    tableLabel: (display, capacity) => `Tisch ${display} (${capacity} Plätze)`,
    guestCount: 'Anzahl der Gäste',
    startOrder: 'Bestellung starten',
    cancel: 'Abbrechen',
    sessionCompletedTitle: 'Die Tischnutzung wurde beendet.',
    sessionExpiredTitle: 'Die Nutzungszeit ist abgelaufen.',
    sessionInvalidTitle: 'Diese Sitzung ist nicht mehr gültig.',
    sessionUnknownTitle: 'Die Sitzung konnte nicht überprüft werden.',
    sessionExpiredInline: 'Die Sitzung ist abgelaufen. Bitte scannen Sie den QR-Code erneut.',
    sessionExpiredDescription: 'Ihre Tischsitzung ist beendet. Bitte scannen Sie den QR-Code erneut, um fortzufahren.',
    sessionExpiredGoHome: 'Zur Startseite',
    rescanQr: 'Erneut scannen',
    sessionTerminalReferenceLabel: 'Referenzcode',
  },
  it: {
    noQrCode: 'Nessun codice QR fornito.',
    pickupNotSupported: 'Questo negozio al momento non supporta il ritiro.',
    unknownQrType: 'Tipo di codice QR non supportato.',
    unknownError: 'Si è verificato un errore sconosciuto.',
    sessionStartFailed: 'Impossibile avviare la sessione.',
    verifying: 'Verifica del codice QR...',
    startingSession: 'Avvio della sessione...',
    retry: 'Riprova',
    goHome: 'Torna alla home',
    tableLabel: (display, capacity) => `Tavolo ${display} (${capacity} posti)`,
    guestCount: 'Numero di ospiti',
    startOrder: 'Inizia ordine',
    cancel: 'Annulla',
    sessionCompletedTitle: 'La sessione al tavolo è terminata.',
    sessionExpiredTitle: 'Il tempo di utilizzo è scaduto.',
    sessionInvalidTitle: 'Questa sessione non è più valida.',
    sessionUnknownTitle: 'Impossibile verificare la sessione.',
    sessionExpiredInline: 'La sessione è scaduta. Scansiona di nuovo il QR.',
    sessionExpiredDescription: 'La sessione del tavolo è terminata. Scansiona di nuovo il codice QR per continuare.',
    sessionExpiredGoHome: 'Vai alla home',
    rescanQr: 'Scansiona di nuovo',
    sessionTerminalReferenceLabel: 'Codice di riferimento',
  },
  ru: {
    noQrCode: 'QR-код не указан.',
    pickupNotSupported: 'Этот магазин сейчас не поддерживает заказы навынос.',
    unknownQrType: 'Неподдерживаемый тип QR-кода.',
    unknownError: 'Произошла неизвестная ошибка.',
    sessionStartFailed: 'Не удалось начать сессию.',
    verifying: 'Проверка QR-кода...',
    startingSession: 'Запуск сессии...',
    retry: 'Повторить',
    goHome: 'Вернуться на главную',
    tableLabel: (display, capacity) => `Стол ${display} (мест: ${capacity})`,
    guestCount: 'Количество гостей',
    startOrder: 'Начать заказ',
    cancel: 'Отмена',
    sessionCompletedTitle: 'Сеанс за столом завершён.',
    sessionExpiredTitle: 'Время использования истекло.',
    sessionInvalidTitle: 'Этот сеанс больше недействителен.',
    sessionUnknownTitle: 'Не удалось проверить сеанс.',
    sessionExpiredInline: 'Сессия истекла. Пожалуйста, отсканируйте QR снова.',
    sessionExpiredDescription: 'Время сессии за столом истекло. Отсканируйте QR-код снова, чтобы продолжить.',
    sessionExpiredGoHome: 'На главную',
    rescanQr: 'Сканировать снова',
    sessionTerminalReferenceLabel: 'Справочный код',
  },
  ar: {
    noQrCode: 'لم يتم تقديم رمز QR.',
    pickupNotSupported: 'هذا المتجر لا يدعم طلبات الاستلام حاليًا.',
    unknownQrType: 'نوع رمز QR غير مدعوم.',
    unknownError: 'حدث خطأ غير معروف.',
    sessionStartFailed: 'تعذر بدء الجلسة.',
    verifying: 'جارٍ التحقق من رمز QR...',
    startingSession: 'جارٍ بدء الجلسة...',
    retry: 'إعادة المحاولة',
    goHome: 'العودة إلى الصفحة الرئيسية',
    tableLabel: (display, capacity) => `الطاولة ${display} (${capacity} مقاعد)`,
    guestCount: 'عدد الضيوف',
    startOrder: 'بدء الطلب',
    cancel: 'إلغاء',
    sessionCompletedTitle: 'انتهى استخدام الطاولة.',
    sessionExpiredTitle: 'انتهى وقت الاستخدام.',
    sessionInvalidTitle: 'لم تعد هذه الجلسة صالحة.',
    sessionUnknownTitle: 'تعذر التحقق من الجلسة.',
    sessionExpiredInline: 'انتهت صلاحية الجلسة. يرجى مسح رمز QR مرة أخرى.',
    sessionExpiredDescription: 'انتهت جلسة الطاولة الخاصة بك. يرجى مسح رمز QR مرة أخرى للمتابعة.',
    sessionExpiredGoHome: 'الذهاب إلى الصفحة الرئيسية',
    rescanQr: 'إعادة المسح',
    sessionTerminalReferenceLabel: 'الرمز المرجعي',
  },
  vi: {
    noQrCode: 'Không có mã QR được cung cấp.',
    pickupNotSupported: 'Cửa hàng này hiện không hỗ trợ đặt món mang đi.',
    unknownQrType: 'Loại mã QR không được hỗ trợ.',
    unknownError: 'Đã xảy ra lỗi không xác định.',
    sessionStartFailed: 'Không thể bắt đầu phiên.',
    verifying: 'Đang xác minh mã QR...',
    startingSession: 'Đang bắt đầu phiên...',
    retry: 'Thử lại',
    goHome: 'Về trang chủ',
    tableLabel: (display, capacity) => `Bàn ${display} (${capacity} chỗ ngồi)`,
    guestCount: 'Số lượng khách',
    startOrder: 'Bắt đầu đặt món',
    cancel: 'Hủy',
    sessionCompletedTitle: 'Phiên sử dụng bàn đã kết thúc.',
    sessionExpiredTitle: 'Thời gian sử dụng đã hết.',
    sessionInvalidTitle: 'Phiên này không còn hiệu lực.',
    sessionUnknownTitle: 'Không thể xác minh phiên.',
    sessionExpiredInline: 'Phiên đã hết hạn. Vui lòng quét lại mã QR.',
    sessionExpiredDescription: 'Phiên sử dụng bàn của quý khách đã kết thúc. Vui lòng quét lại mã QR để tiếp tục.',
    sessionExpiredGoHome: 'Đi đến trang chủ',
    rescanQr: 'Quét lại',
    sessionTerminalReferenceLabel: 'Mã tham chiếu',
  },
  th: {
    noQrCode: 'ไม่มีรหัส QR',
    pickupNotSupported: 'ร้านนี้ยังไม่รองรับการสั่งแบบรับเองในขณะนี้',
    unknownQrType: 'ประเภทรหัส QR ไม่รองรับ',
    unknownError: 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
    sessionStartFailed: 'ไม่สามารถเริ่มเซสชันได้',
    verifying: 'กำลังตรวจสอบรหัส QR...',
    startingSession: 'กำลังเริ่มเซสชัน...',
    retry: 'ลองอีกครั้ง',
    goHome: 'กลับสู่หน้าหลัก',
    tableLabel: (display, capacity) => `โต๊ะ ${display} (${capacity} ที่นั่ง)`,
    guestCount: 'จำนวนแขก',
    startOrder: 'เริ่มสั่งอาหาร',
    cancel: 'ยกเลิก',
    sessionCompletedTitle: 'การใช้โต๊ะสิ้นสุดแล้ว',
    sessionExpiredTitle: 'หมดเวลาใช้งานแล้ว',
    sessionInvalidTitle: 'เซสชันนี้ไม่สามารถใช้งานได้อีกต่อไป',
    sessionUnknownTitle: 'ไม่สามารถตรวจสอบเซสชันได้',
    sessionExpiredInline: 'เซสชันหมดอายุแล้ว กรุณาสแกน QR อีกครั้ง',
    sessionExpiredDescription: 'เซสชันโต๊ะของคุณสิ้นสุดแล้ว กรุณาสแกนรหัส QR อีกครั้งเพื่อดำเนินการต่อ',
    sessionExpiredGoHome: 'ไปที่หน้าหลัก',
    rescanQr: 'สแกนอีกครั้ง',
    sessionTerminalReferenceLabel: 'รหัสอ้างอิง',
  },
  nl: {
    noQrCode: 'Geen QR-code opgegeven.',
    pickupNotSupported: 'Deze winkel ondersteunt momenteel geen afhaalbestellingen.',
    unknownQrType: 'Niet-ondersteund type QR-code.',
    unknownError: 'Er is een onbekende fout opgetreden.',
    sessionStartFailed: 'Kon de sessie niet starten.',
    verifying: 'QR-code wordt geverifieerd...',
    startingSession: 'Sessie wordt gestart...',
    retry: 'Opnieuw proberen',
    goHome: 'Terug naar home',
    tableLabel: (display, capacity) => `Tafel ${display} (${capacity} zitplaatsen)`,
    guestCount: 'Aantal gasten',
    startOrder: 'Bestelling starten',
    cancel: 'Annuleren',
    sessionCompletedTitle: 'De tafelsessie is beëindigd.',
    sessionExpiredTitle: 'De gebruikstijd is verstreken.',
    sessionInvalidTitle: 'Deze sessie is niet meer geldig.',
    sessionUnknownTitle: 'De sessie kon niet worden geverifieerd.',
    sessionExpiredInline: 'De sessie is verlopen. Scan de QR opnieuw.',
    sessionExpiredDescription: 'Uw tafelsessie is beëindigd. Scan de QR-code opnieuw om door te gaan.',
    sessionExpiredGoHome: 'Naar home',
    rescanQr: 'Opnieuw scannen',
    sessionTerminalReferenceLabel: 'Referentiecode',
  },
};

// #522 — Locale 전수 정의로 파라미터를 Locale 로 확장 (fallback es 유지, Language ⊂ Locale 하위호환).
export function getQrSessionTranslation(
  locale: Locale | null | undefined,
  fallback: Locale = 'es',
): QrSessionTranslation {
  return qrSessionTranslations[locale ?? fallback] ?? qrSessionTranslations[fallback];
}
